import { useCallback, useEffect, useMemo, useState } from "react"
import type {
	AdminClient,
	AssociationMetricsRecord,
	AssociationStagingItem
} from "../../../api/admin-client.ts"

/**
 * Phase 4-metrics + Phase 4-batch 운영 탭.
 *
 * - 좌측: 시계열 차트 (alias/association/staging 카운트 30일) — 단순 SVG polyline
 * - 우측 상단: batch trigger 버튼 4종 (PMI / embedding-sim / LLM-infer / metrics record)
 * - 우측 하단: staging pending queue (top 50)
 */
export function AssociationsTab(props: { readonly client: AdminClient; readonly slug: string }) {
	const { client, slug } = props
	const [timeline, setTimeline] = useState<ReadonlyArray<AssociationMetricsRecord>>([])
	const [staging, setStaging] = useState<ReadonlyArray<AssociationStagingItem>>([])
	const [busy, setBusy] = useState<string | undefined>()
	const [error, setError] = useState<string | undefined>()
	const [lastResult, setLastResult] = useState<string | undefined>()
	const [llmText, setLlmText] = useState<string>("")

	const refresh = useCallback(async () => {
		setError(undefined)
		try {
			const [tl, sg] = await Promise.all([
				client.getAssociationMetricsTimeline(slug, 30),
				client.listAssociationStaging(slug, { status: "pending", limit: 50 })
			])
			setTimeline(tl)
			setStaging(sg)
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err))
		}
	}, [client, slug])

	useEffect(() => {
		void refresh()
	}, [refresh])

	const runAction = useCallback(
		async (label: string, fn: () => Promise<unknown>) => {
			setBusy(label)
			setError(undefined)
			setLastResult(undefined)
			try {
				const result = await fn()
				setLastResult(`${label}: ${JSON.stringify(result)}`)
				await refresh()
			} catch (err) {
				setError(err instanceof Error ? err.message : String(err))
			} finally {
				setBusy(undefined)
			}
		},
		[refresh]
	)

	return (
		<div className="stack stack-lg">
			<header className="stack stack-sm">
				<h3>Associations — Phase 4</h3>
				<p className="muted">
					자동 추출 워커 (co-occurrence PMI · embedding similarity · LLM-infer) + 일자별 카운트 스냅샷.
					Promote 결정은 staging 큐에서 별도 진행.
				</p>
			</header>

			{error && (
				<div className="card card-danger" role="alert">
					{error}
				</div>
			)}
			{lastResult && <output className="card card-info">{lastResult}</output>}

			<section className="stack stack-md">
				<h4>워커 트리거 + 스냅샷 record</h4>
				<div className="cluster">
					<button
						type="button"
						className="btn btn-sm"
						disabled={busy !== undefined}
						onClick={() => runAction("co-occurrence", () => client.runCoOccurrencePmi(slug))}
					>
						{busy === "co-occurrence" ? "실행 중…" : "co-occurrence (PMI)"}
					</button>
					<button
						type="button"
						className="btn btn-sm"
						disabled={busy !== undefined}
						onClick={() => runAction("embedding-similarity", () => client.runEmbeddingSimilarity(slug))}
					>
						{busy === "embedding-similarity" ? "실행 중…" : "embedding-similarity"}
					</button>
					<button
						type="button"
						className="btn btn-sm"
						disabled={busy !== undefined}
						onClick={() => runAction("metrics record", () => client.recordAssociationMetrics(slug))}
					>
						{busy === "metrics record" ? "실행 중…" : "metrics record"}
					</button>
				</div>
				<div className="stack stack-xs">
					<label htmlFor="llm-text" className="muted">
						LLM-infer 입력 텍스트 (Anthropic Sonnet — 비용 큰 호출)
					</label>
					<textarea
						id="llm-text"
						value={llmText}
						onChange={(e) => setLlmText(e.target.value)}
						rows={3}
						placeholder="텍스트를 붙여넣으면 슬라이스 안 relations 가 staging 에 적재됩니다 (hintSource: llm-inferred, weight 0.7)."
					/>
					<button
						type="button"
						className="btn btn-sm"
						disabled={busy !== undefined || llmText.trim().length === 0}
						onClick={() => runAction("llm-infer", () => client.runLlmInfer(slug, llmText.trim()))}
					>
						{busy === "llm-infer" ? "실행 중…" : "llm-infer"}
					</button>
				</div>
			</section>

			<section className="stack stack-md">
				<h4>일자별 카운트 시계열 (최근 30일)</h4>
				{timeline.length === 0 ? (
					<p className="muted">
						아직 record 된 스냅샷이 없습니다 — 위의 "metrics record" 를 한 번 실행하세요.
					</p>
				) : (
					<TimelineChart timeline={timeline} />
				)}
			</section>

			<section className="stack stack-md">
				<h4>Staging 대기열 (pending top 50)</h4>
				{staging.length === 0 ? <p className="muted">pending row 없음.</p> : <StagingTable items={staging} />}
			</section>
		</div>
	)
}

/**
 * 단순 SVG line chart — 외부 차트 라이브러리 없이 직접 polyline.
 *
 * 5 metric (alias / association / pending / promoted / rejected) 시계열을 동일 viewBox 에 겹쳐 그림.
 * y 축은 전체 max 기준 정규화, x 축은 recordedDate 순.
 */
function TimelineChart(props: { readonly timeline: ReadonlyArray<AssociationMetricsRecord> }) {
	const { timeline } = props
	// 시계열은 서버에서 DESC — 차트 그릴 때는 오래된 → 최신 순으로 reverse
	const sorted = useMemo(() => [...timeline].reverse(), [timeline])
	const maxValue = useMemo(() => {
		let m = 0
		for (const r of sorted) {
			m = Math.max(
				m,
				r.aliasCount,
				r.associationCount,
				r.stagingPendingCount,
				r.stagingPromotedCount,
				r.stagingRejectedCount
			)
		}
		return m === 0 ? 1 : m
	}, [sorted])
	const W = 600
	const H = 200
	const padX = 30
	const padY = 16
	const innerW = W - padX * 2
	const innerH = H - padY * 2
	const xStep = sorted.length > 1 ? innerW / (sorted.length - 1) : 0

	const series: ReadonlyArray<{
		readonly key: string
		readonly label: string
		readonly color: string
		readonly values: ReadonlyArray<number>
	}> = [
		{ key: "alias", label: "alias", color: "var(--pk-brand-500)", values: sorted.map((r) => r.aliasCount) },
		{
			key: "association",
			label: "association",
			color: "var(--pk-success-500)",
			values: sorted.map((r) => r.associationCount)
		},
		{
			key: "pending",
			label: "staging pending",
			color: "var(--pk-warning-500)",
			values: sorted.map((r) => r.stagingPendingCount)
		},
		{
			key: "promoted",
			label: "staging promoted",
			color: "var(--pk-info-500)",
			values: sorted.map((r) => r.stagingPromotedCount)
		},
		{
			key: "rejected",
			label: "staging rejected",
			color: "var(--pk-danger-500)",
			values: sorted.map((r) => r.stagingRejectedCount)
		}
	]

	return (
		<div className="stack stack-sm">
			<svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Association metrics timeline">
				<title>일자별 카운트 시계열</title>
				{/* y-axis grid (0, max/2, max) */}
				{[0, 0.5, 1].map((t) => {
					const y = padY + innerH * (1 - t)
					return (
						<line key={t} x1={padX} y1={y} x2={W - padX} y2={y} stroke="var(--pk-gray-200)" strokeWidth={1} />
					)
				})}
				{series.map((s) => {
					const points = s.values
						.map((v, i) => {
							const x = padX + xStep * i
							const y = padY + innerH * (1 - v / maxValue)
							return `${x},${y}`
						})
						.join(" ")
					return (
						<polyline
							key={s.key}
							fill="none"
							stroke={s.color}
							strokeWidth={2}
							points={points}
							aria-label={s.label}
						/>
					)
				})}
				<text x={padX} y={padY - 4} fontSize={10} fill="var(--pk-gray-500)">
					{maxValue}
				</text>
				<text x={padX} y={H - 4} fontSize={10} fill="var(--pk-gray-500)">
					0
				</text>
			</svg>
			<div className="cluster">
				{series.map((s) => (
					<span key={s.key} className="cluster cluster-xs">
						<span style={{ background: s.color, width: 12, height: 12, display: "inline-block" }} />
						<span className="muted">{s.label}</span>
					</span>
				))}
			</div>
		</div>
	)
}

function StagingTable(props: { readonly items: ReadonlyArray<AssociationStagingItem> }) {
	return (
		<table className="table table-sm">
			<thead>
				<tr>
					<th>from</th>
					<th>to</th>
					<th>kind</th>
					<th>weight</th>
					<th>evidence</th>
					<th>submittedAt</th>
				</tr>
			</thead>
			<tbody>
				{props.items.map((it) => (
					<tr key={it.id}>
						<td>
							<code className="inline">{shortNodeId(it.fromNodeId)}</code>
						</td>
						<td>
							<code className="inline">{shortNodeId(it.toNodeId)}</code>
						</td>
						<td>{it.hintSource}</td>
						<td>{it.candidateWeight.toFixed(3)}</td>
						<td>{it.evidenceCount}</td>
						<td className="muted">{it.submittedAt}</td>
					</tr>
				))}
			</tbody>
		</table>
	)
}

function shortNodeId(nodeId: string): string {
	const parts = nodeId.split(":")
	if (parts.length !== 4) return nodeId
	const [, , , identifier] = parts as [string, string, string, string]
	return identifier
}
